import {
  type ColumnFiltersState,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  type Table as TanStackTable,
  useReactTable,
} from '@tanstack/react-table'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { COMPONENT_HEADER_HEIGHT, PADDING_GAP, TABLE_HEADER_HEIGHT } from '@/catalog-apps/components/config'
import { useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { DataTableCellExpansionProvider, ExpandableCellContent } from './DataTableCellExpansion'

// Threshold for enabling virtualization - only virtualize if we have more than this many rows
// const VIRTUALIZATION_THRESHOLD = 100 // TODO: Temporarily disabled

interface DataTableProps<TData, TValue> {
  title?: React.ReactNode
  description?: string
  action?: React.ReactNode
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  initialGlobalFilter?: string
  initialColumnFilters?: ColumnFiltersState
  defaultSortColumnId?: string
  defaultSortDesc?: boolean
  filters?: React.ReactNode | ((table: TanStackTable<TData>) => React.ReactNode)
}

export function DataTable<TData, TValue>({
  columns,
  data,
  title,
  description,
  action,
  initialGlobalFilter,
  initialColumnFilters,
  defaultSortColumnId,
  defaultSortDesc,
  filters,
}: DataTableProps<TData, TValue>) {
  // Create a reference to the table body
  const tableBodyRef = useRef<HTMLTableSectionElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter ?? '')
  const initialSorting: SortingState = defaultSortColumnId ? [{ id: defaultSortColumnId, desc: !!defaultSortDesc }] : []
  const [sorting, setSorting] = useState<SortingState>(initialSorting)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters ?? [])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
      sorting,
      columnFilters,
    },
  })

  // Get filtered rows
  const { rows } = table.getRowModel()
  // const shouldVirtualize = rows.length > VIRTUALIZATION_THRESHOLD // TODO: Temporarily disabled
  const shouldVirtualize = false

  // Set up the virtualizer only if we need it
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollAreaRef.current,
    estimateSize: () => 45, // Estimate row height (adjust based on your actual row height)
    overscan: 5, // Number of items to render before/after the visible area
    enabled: shouldVirtualize,
  })

  // Get virtual rows only if virtualizing
  const virtualRows = shouldVirtualize ? rowVirtualizer.getVirtualItems() : []
  const paddingTop = shouldVirtualize && virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom =
    shouldVirtualize && virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0

  const renderCellContent = (cellId: string, content: React.ReactNode, disableTruncate?: boolean) => {
    if (disableTruncate) {
      return content
    }

    return <ExpandableCellContent cellId={cellId}>{content}</ExpandableCellContent>
  }

  return (
    <DataTableCellExpansionProvider>
      <ComponentHeader className="border-b-0 pt-5" wrapperClassName="border-b-0">
        <ComponentTitle title={title} description={description} />
        <div className="ml-auto mr-0 flex items-center gap-2">
          {typeof filters === 'function' ? filters(table) : filters}
          <SearchInput
            value={globalFilter}
            onChange={(value) => {
              setGlobalFilter(value)
            }}
          />
          {action}
        </div>
      </ComponentHeader>
      <div
        style={{ height: `calc(100% - ${COMPONENT_HEADER_HEIGHT + PADDING_GAP}px` }}
        className="overflow-x-auto rounded-md border bg-background shadow-lg"
      >
        <div className="h-full min-w-min overflow-hidden">
          {/* Sticky Header */}
          <div style={{ height: TABLE_HEADER_HEIGHT }} className="relative [&>div]:overflow-hidden">
            <Table className="w-full table-fixed border-collapse">
              <colgroup>
                {table.getAllColumns().map((column) => (
                  <col
                    key={column.id}
                    style={{ width: column.columnDef.size ? `${column.columnDef.size}px` : `${100 / columns.length}%` }}
                  />
                ))}
              </colgroup>
              <TableHeader className="sticky top-0 bg-offwhite">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="shadow-[0_1px_2px_0_rgba(0,0,0,0.1)]">
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead className="px-2 font-bold" key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
          </div>

          {/* Scrollable Body */}
          <ScrollArea
            style={{ height: `calc(100% - ${TABLE_HEADER_HEIGHT}px)` }}
            ref={scrollAreaRef}
            scrollHideDelay={0}
          >
            <Table className="w-full table-fixed border-collapse">
              <colgroup>
                {table.getAllColumns().map((column) => (
                  <col
                    key={column.id}
                    style={{ width: column.columnDef.size ? `${column.columnDef.size}px` : `${100 / columns.length}%` }}
                  />
                ))}
              </colgroup>
              <TableBody className="bg-background" ref={tableBodyRef}>
                {rows.length > 0 ? (
                  shouldVirtualize ? (
                    // Virtualized rendering for large datasets
                    <>
                      {paddingTop > 0 && (
                        <tr>
                          <td style={{ height: `${paddingTop}px` }} colSpan={columns.length} />
                        </tr>
                      )}

                      {virtualRows.map((virtualRow) => {
                        const row = rows[virtualRow.index]
                        return (
                          <TableRow
                            className="border-none even:bg-offwhite/60 hover:bg-primary/5"
                            key={row.id}
                            data-state={row.getIsSelected() && 'selected'}
                            data-index={virtualRow.index}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell className="max-w-0 overflow-hidden px-2 py-2 align-top" key={cell.id}>
                                {renderCellContent(
                                  cell.id,
                                  flexRender(cell.column.columnDef.cell, cell.getContext()),
                                  cell.column.columnDef.meta?.disableCellTruncate
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        )
                      })}

                      {paddingBottom > 0 && (
                        <tr>
                          <td style={{ height: `${paddingBottom}px` }} colSpan={columns.length} />
                        </tr>
                      )}
                    </>
                  ) : (
                    // Non-virtualized rendering for small datasets
                    rows.map((row) => (
                      <TableRow
                        className="border-none even:bg-offwhite/60 hover:bg-primary/5"
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell className="max-w-0 overflow-hidden px-2 py-2 align-top" key={cell.id}>
                            {renderCellContent(
                              cell.id,
                              flexRender(cell.column.columnDef.cell, cell.getContext()),
                              cell.column.columnDef.meta?.disableCellTruncate
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      </div>
    </DataTableCellExpansionProvider>
  )
}
